<template>
  <v-main>
    <v-container fluid grid-list-md>
      <v-layout row wrap>
        <v-flex sx12>
          <h1 class="mt-n2 mb-4">{{$t('Settings')}}</h1>
          <v-progress-linear v-if="loading" :indeterminate="true"></v-progress-linear>
          <v-flex v-else>
            <v-card raised class="mb-3">
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('System')}} : {{$t('Settings')}}</h3>
                  <v-checkbox :label="$t('Enable 2-steps authentication')" v-model="form.sysEnable2StepsAuthentication" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show facility name instead of facility no')" v-model="form.sysUseFacilityDescription" :color="'primary'"></v-checkbox>
                  <v-text-field v-model="form.sysMinPassWordLenght" :label="$t('Minimum amount of characters in password (5-16)')"></v-text-field>
                  <v-text-field v-model="form.sysPassWordRegularExpression" :label="$t('Optional other password rules (regular expression)')"></v-text-field>
                  <v-flex d-flex>Example: ^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|&lt;>]).*$</v-flex>
                  <v-flex d-flex>{{$t("At least one uppercase letter. At least one lowercase letter. At least one diget. At least one special character")}}</v-flex>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card v-if="hasDeliveryInspectionClaim" raised class="mb-3">
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Delivery inspection')}} : {{$t('Settings')}}</h3>
                  <v-checkbox :label="$t('Hide Delivery Inspection Skip button')" v-model="form.deliveryInspectionHideSkip" :color="'primary'"></v-checkbox>
                  <v-text-field v-model="form.deliveryInspectionEmailHeader" :readonly="false" :label="$t('Email Header')"></v-text-field>
                  <v-textarea
                                outlined
                                name="input-7-1"
                                label="Email Body"
                                value="form.deliveryInspectionEmailBody"
                                v-model="form.deliveryInspectionEmailBody"
                                hint="">
                  </v-textarea>
                  <v-text-field v-model="form.deliveryInspectionEmailSender" :readonly="false" :label="$t('Email Sender')"></v-text-field>
                  <v-checkbox :label="$t('Show Delivery Inspection Disable Vehicle button')" v-model="form.deliveryInspectionShowDisable" :color="'primary'"></v-checkbox>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card v-if="hasWheelLoaderClaim" raised class="mb-3">
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Wheel loader')}} : {{$t('Settings')}}</h3>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.wheelLoaderFieldsHidden" :label="$t('Hidden')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.wheelLoaderFieldsMandatory" :label="$t('Mandatory')" :items="validFields"  :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.wheelLoaderFieldsReadOnly" :label="$t('Readonly')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-checkbox :label="$t('Enable VT25 Weight integration (show Wheel loader transfer button)')" v-model="form.wheelLoaderEnableVT25WeightIntegration" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Disable Wheel loader New and Edit buttons')" v-model="form.wheelLoaderDisableButtons" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Disable Wheel loader Create Vehicle button')" v-model="form.wheelLoaderDisableCreateVehicle" :color="'primary'"></v-checkbox>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card v-if="hasPreRegistrationClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Pre-registration')}} : {{$t('Settings')}}</h3>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.preRegistrationFieldsHidden" :label="$t('Hidden')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.preRegistrationFieldsMandatory" :label="$t('Mandatory')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.preRegistrationFieldsReadOnly" :label="$t('Readonly')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-text-field v-model="form.preRegistrationGPSPosition" :label="$t('GPS Position (latitude, longitude)')" :rules="[rules.gpsPosition]">
                      <template v-slot:append>
                        <v-btn class="mb-1" color="success" :href="'https://www.google.com/maps/place/' + form.preRegistrationGPSPosition" target="_blank"><v-icon>place</v-icon></v-btn>
                      </template>
                  </v-text-field>
                  <v-text-field v-model="form.preRegistrationGPSAcceptedDistanceKm" :label="$t('GPS Accepted Distance (Km)')" :rules="[(form.preRegistrationGPSAcceptedDistanceKm && form.preRegistrationGPSAcceptedDistanceKm >= 0 && form.preRegistrationGPSAcceptedDistanceKm <= 100) || this.$t('Invalid. Must be between 0-100 Km')]"></v-text-field>
                  <v-text-field v-model="form.preRegistrationExpiredTimeoutHours" :label="$t('Expired Timeout (Hours)')" :rules="[(form.preRegistrationExpiredTimeoutHours && form.preRegistrationExpiredTimeoutHours >= 0 && form.preRegistrationExpiredTimeoutHours <= 24) || this.$t('Invalid. Must be between 0-24 hours')]"></v-text-field>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasScanCodeClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('scan-code-registrations')}} : {{$t('Settings')}}</h3>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeFieldsHidden" :label="$t('Hidden')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeFieldsMandatory" :label="$t('Mandatory')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeFieldsReadOnly" :label="$t('Readonly')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasScanCodeClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('scan-code-deliveries')}} : {{$t('Settings')}}</h3>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeDelivFieldsHidden" :label="$t('Hidden')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeDelivFieldsMandatory" :label="$t('Mandatory')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanCodeDelivFieldsReadOnly" :label="$t('Readonly')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasScanMobileClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('scan-mobile-registrations')}} : {{$t('Settings')}}</h3>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanMobileFieldsHidden" :label="$t('Hidden')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanMobileFieldsMandatory" :label="$t('Mandatory')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-select chips multiple deletable-chips auto-select-first v-model="form.scanMobileFieldsReadOnly" :label="$t('Readonly')" :items="validFields" :item-text="item => $t(item.text)" :no-data-text="ct.NoDataAvailable"></v-select>
                  <v-checkbox :label="$t('Show enter weight button')" v-model="form.scanMobileShowEnterButton" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show dialog for -Do you want to finish weighing?')" v-model="form.scanMobileShowFinishDialog" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show dialog for -Do you want to cancel weighing?')" v-model="form.scanMobileShowCancelDialog" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show dialog for -Do you want to use xxx kg as empty weight?')" v-model="form.scanMobileShowUseTareDialog" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show dialog for -Do you want to remove the temporary empty weight?')" v-model="form.scanMobileShowRemoveTareDialog" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show transfer registration checkbox')" v-model="form.scanMobileShowTransferReg" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Disable Create Vehicle button')" v-model="form.scanMobileDisableCreateVehicle" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Image capture visual delay info')" v-model="form.scanMobileCamDelayEnabled" :color="'primary'"></v-checkbox>
                  <v-text-field v-model="form.scanMobileCamDelay" :label="$t('Image capture delay (Seconds)')" :rules="[(form.scanMobileCamDelay && form.scanMobileCamDelay >= 5 && form.scanMobileCamDelay <= 24) || this.$t('Invalid. Must be between 5-24 Seconds')]"></v-text-field>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasClassifierClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Classifier')}} : {{$t('Settings')}}</h3>
                  <v-text-field v-model="form.classifierLoadRegistrationsDaysBack" :label="$t('Load registrations days back (Days)')"
                  :rules="[(form.classifierLoadRegistrationsDaysBack && form.classifierLoadRegistrationsDaysBack >= 1 && form.classifierLoadRegistrationsDaysBack <= 365) || this.$t('Invalid. Must be between 1-365 days')]"></v-text-field>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasReportsClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Reports')}} : {{$t('Settings')}}</h3>
                  <v-checkbox :label="$t('Exclude registrations with first weight higher than second checkbox')" v-model="form.ReportsShowOnlyOutGoingRegs" :color="'primary'"></v-checkbox>
                  <v-checkbox :label="$t('Show column Print Corfirmation')" v-model="form.ShowPrintConfirmation" :color="'primary'"></v-checkbox>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasCreateVehicleClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Create Vehicles')}} : {{$t('Settings')}}</h3>
                  <v-checkbox :label="$t('Vehicle user Inherit the valid facilities, from the user who creates.')" v-model="form.VehicleUserInheritFacilities" :color="'primary'"></v-checkbox>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasBatchRegistrationClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Batch registration')}} : {{$t('Settings')}}</h3>
                    <v-text-field v-model="form.BatchRegistrationMinWeightWarning" :label="$t('Minimum Weight warning')"></v-text-field>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card class="mt-4" v-if="hasApiImportExportClaim" raised>
              <v-card-text>
                <v-form ref="form">
                  <v-layout justify-end></v-layout>
                  <h3 class="mb-4">{{$t('Import/Export API')}} : {{$t('Settings')}}</h3>
                      <v-text-field v-model="form.apiKey" :readonly="true" :label="$t('API Key')">
                        <template v-slot:append>
                            <v-btn class="mb-1" small @click="setGuid" color="primary">{{$t("Generate API key")}}</v-btn>
                          </template>
                        </v-text-field>
                    <v-text-field v-model="form.apiImportExportUrl" :label="$t('Notify API URL')"></v-text-field>
                    <v-text-field v-model="form.apiImportExportGuid" :label="$t('Notify API key')"></v-text-field>
                    <v-checkbox :label="$t('Include data in notification')" v-model="form.apiImportIncludeData" :color="'primary'"></v-checkbox>
                </v-form>
              </v-card-text>
            </v-card>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="mt-2" large :loading="submitting" @click="submit" color="success">{{$t("Save")}}</v-btn>
            </v-card-actions>
          </v-flex>
        </v-flex>
      </v-layout>
    </v-container>
  </v-main>
</template>

<script>
import { app } from '@/main'
import ct from '@/core/mixins/common-translations'
import rules from '@/core/mixins/common-rules'
import * as License from '@/core/license'
import { CorporationDetail } from '@/core/corporationDetail'
import * as Security from '@/core/security'

export default {
  mixins: [ct, rules], // , preventLeave

  data: () => ({
    dataType: 'settings',
    loading: true,
    submitting: false,
    valid: true,

    // Remember to add new claim check in loadSettings
    hasDeliveryInspectionClaim: false,
    hasWheelLoaderClaim: false,
    hasPreRegistrationClaim: false,
    hasScanCodeClaim: false,
    hasScanMobileClaim: false,
    hasClassifierClaim: false,
    hasReportsClaim: false,
    hasBatchRegistrationClaim: false,
    hasApiImportExportClaim: false,
    hasCreateVehicleClaim: false,

    form: {
      sysEnable2StepsAuthentication: false,
      sysUseFacilityDescription: false,
      sysMinPassWordLenght: 5,
      sysPassWordRegularExpression: '',

      deliveryInspectionHideSkip: false,
      deliveryInspectionEmailHeader: 'ScanPortal - Inspection for {VehicleLicensePlateNo}',
      deliveryInspectionEmailBody: '<h3><strong><span style=font-family:tahoma,geneva,sans-serif;>{VehicleLicensePlateNo} is ready for Inspection.</span></strong></h3><h3><span style=font-family:tahoma,geneva,sans-serif;>Link: <a href=" + link + @"title=Go to inspection>" + link + @"</a></span></h3><p><span style=font-family:tahoma,geneva,sans-serif;>Kind regards,</span></p><p><span style=font-family:tahoma,geneva,sans-serif;>ScanPortal.net</span></p>',
      deliveryInspectionEmailSender: 'scanportal@gmail.com',
      deliveryInspectionShowDisable: false,

      wheelLoaderFieldsHidden: [],
      wheelLoaderFieldsMandatory: [],
      wheelLoaderFieldsReadOnly: [],
      wheelLoaderEnableVT25WeightIntegration: null,
      wheelLoaderDisableButtons: false,

      preRegistrationFieldsHidden: [],
      preRegistrationFieldsMandatory: [],
      preRegistrationFieldsReadOnly: [],
      preRegistrationGPSPosition: '',
      preRegistrationGPSAcceptedDistanceKm: null,
      preRegistrationExpiredTimeoutHours: null,

      scanCodeFieldsHidden: [],
      scanCodeFieldsMandatory: [],
      scanCodeFieldsReadOnly: [],

      scanCodeDelivFieldsHidden: [],
      scanCodeDelivFieldsMandatory: [],
      scanCodeDelivFieldsReadOnly: [],

      active: false,

      scanMobileFieldsHidden: [],
      scanMobileFieldsMandatory: [],
      scanMobileFieldsReadOnly: [],
      scanMobileShowEnterButton: true,
      scanMobileShowFinishDialog: true,
      scanMobileShowUseTareDialog: true,
      scanMobileShowRemoveTareDialog: true,
      scanMobileShowCancelDialog: true,

      classifierLoadRegistrationsDaysBack: 7,

      ReportsShowOnlyOutGoingRegs: false,
      ShowPrintConfirmation: false,
      VehicleUserInheritFacilities: false,

      BatchRegistrationMinWeightWarning: 800,

      scanMobileShowTransferReg: false,

      apiImportExportUrl: 'https://domain_at_corp/api_at_corp',

      apiImportExportGuid: '00000000-0000-0000-0000-000000000000',

      apiKey: '00000000-0000-0000-0000-000000000000',

      apiImportIncludeData: false

    },
    sysEnable2StepsAuthenticationDetail: null,
    sysUseFacilityDescriptionDetail: null,
    sysMinPassWordLenghtDetail: null,
    sysPassWordRegularExpression: null,

    deliveryInspectionHideSkipDetail: null,
    deliveryInspectionEmailHeaderDetail: null,
    deliveryInspectionEmailBodyDetail: null,
    deliveryInspectionEmailSenderDetail: null,
    deliveryInspectionShowDisableDetail: null,

    wheelLoaderFieldsHiddenDetail: null,
    wheelLoaderFieldsMandatoryDetail: null,
    wheelLoaderFieldsReadOnlyDetail: null,
    wheelLoaderEnableVT25WeightIntegrationDetail: null,
    wheelLoaderDisableButtonsDetail: null,
    wheelLoaderDisableCreateVehicleDetail: null,

    preRegistrationFieldsHiddenDetail: null,
    preRegistrationFieldsMandatoryDetail: null,
    preRegistrationFieldsReadOnlyDetail: null,
    preRegistrationGPSPositionDetail: null,
    preRegistrationGPSAcceptedDistanceKmDetail: null,
    preRegistrationExpiredTimeoutHoursDetail: null,

    scanCodeFieldsHiddenDetail: null,
    scanCodeFieldsMandatoryDetail: null,
    scanCodeFieldsReadOnlyDetail: null,

    scanCodeDelivFieldsHiddenDetail: null,
    scanCodeDelivFieldsMandatoryDetail: null,
    scanCodeDelivFieldsReadOnlyDetail: null,

    scanMobileFieldsHiddenDetail: null,
    scanMobileFieldsMandatoryDetail: null,
    scanMobileFieldsReadOnlyDetail: null,
    scanMobileShowEnterButtonDetail: null,
    scanMobileShowFinishDialogDetail: null,
    scanMobileShowUseTareDialogDetail: null,
    scanMobileShowRemoveTareDialogDetail: null,
    scanMobileShowCancelDialogDetail: null,
    scanMobileDisableCreateVehicleDetail: null,
    scanMobileCamDelayEnabledDetail: null,
    scanMobileCamDelayDetail: null,

    classifierLoadRegistrationsDaysBackDetail: null,

    scanMobileShowTransferRegDetail: null,

    ReportsShowOnlyOutGoingRegsDetail: null,
    ShowPrintConfirmationDetail: null,

    VehicleUserInheritFacilitiesDetail: null,

    BatchRegistrationMinWeightWarningDetail: null,

    validFields: [
      { text: 'Facility', value: 'Facility' },
      { text: 'Vehicle', value: 'Vehicle' },
      { text: 'Agreement', value: 'Agreement' },
      { text: 'Delivery', value: 'Delivery' },
      { text: 'Customer', value: 'Customer' },
      { text: 'Article', value: 'Article' },
      { text: 'RequisitionNo1', value: 'RequisitionNo1' },
      { text: 'RequisitionNo2', value: 'RequisitionNo2' },
      { text: 'Plant', value: 'Plant' },
      { text: 'Carrier', value: 'Carrier' },
      { text: 'Comment', value: 'Comment' },
      { text: 'Storage', value: 'Storage' }
    ]
  }),
  methods: {
    async submit () {
      this.preventLeave = false
      if (this.$refs.form.validate()) {
        this.submitting = true

        await this.saveSettings()

        this.$root.$message.showSuccess(this.$t('Saved'))
        this.submitting = false
      }
    },
    async saveSettings () {
      const settings = []
      let i = 0
      // Convert to string array, if not already done by user (by v-select component change)
      if (this.wheelLoaderFieldsHiddenDetail == null) {
        this.wheelLoaderFieldsHiddenDetail = await CorporationDetail.loadFromUser('WheelLoader_Fields_Hidden')
      }

      if (this.form.wheelLoaderFieldsHidden.toString().includes('[')) this.form.wheelLoaderFieldsHidden = this.form.wheelLoaderFieldsHidden.map(item => item.value)
      this.wheelLoaderFieldsHiddenDetail.Data1 = this.form.wheelLoaderFieldsHidden.toString() // Convert to comma separated values
      settings[i++] = this.wheelLoaderFieldsHiddenDetail

      if (this.wheelLoaderFieldsMandatoryDetail == null) {
        this.wheelLoaderFieldsMandatoryDetail = await CorporationDetail.loadFromUser('WheelLoader_Fields_Mandatory')
      }

      if (this.form.wheelLoaderFieldsMandatory.toString().includes('[')) this.form.wheelLoaderFieldsMandatory = this.form.wheelLoaderFieldsMandatory.map(item => item.value)
      this.wheelLoaderFieldsMandatoryDetail.Data1 = this.form.wheelLoaderFieldsMandatory.toString()
      settings[i++] = this.wheelLoaderFieldsMandatoryDetail

      if (this.wheelLoaderFieldsReadOnlyDetail == null) {
        this.wheelLoaderFieldsReadOnlyDetail = await CorporationDetail.loadFromUser('WheelLoader_Fields_ReadOnly')
      }

      if (this.form.wheelLoaderFieldsReadOnly.toString().includes('[')) this.form.wheelLoaderFieldsReadOnly = this.form.wheelLoaderFieldsReadOnly.map(item => item.value)
      this.wheelLoaderFieldsReadOnlyDetail.Data1 = this.form.wheelLoaderFieldsReadOnly.toString()
      settings[i++] = this.wheelLoaderFieldsReadOnlyDetail

      if (this.scanCodeFieldsHiddenDetail == null) {
        this.scanCodeFieldsHiddenDetail = await CorporationDetail.loadFromUser('ScanCode_Fields_Hidden')
      }

      if (this.form.scanCodeFieldsHidden.toString().includes('[')) this.form.scanCodeFieldsHidden = this.form.scanCodeFieldsHidden.map(item => item.value)
      this.scanCodeFieldsHiddenDetail.Data1 = this.form.scanCodeFieldsHidden.toString()
      settings[i++] = this.scanCodeFieldsHiddenDetail

      if (this.scanCodeFieldsMandatoryDetail == null) {
        this.scanCodeFieldsMandatoryDetail = await CorporationDetail.loadFromUser('ScanCode_Fields_Mandatory')
      }

      if (this.form.scanCodeFieldsMandatory.toString().includes('[')) this.form.scanCodeFieldsMandatory = this.form.scanCodeFieldsMandatory.map(item => item.value)
      this.scanCodeFieldsMandatoryDetail.Data1 = this.form.scanCodeFieldsMandatory.toString()
      settings[i++] = this.scanCodeFieldsMandatoryDetail

      if (this.scanCodeFieldsReadOnlyDetail == null) {
        this.scanCodeFieldsReadOnlyDetail = await CorporationDetail.loadFromUser('ScanCode_Fields_ReadOnly')
      }

      if (this.form.scanCodeFieldsReadOnly.toString().includes('[')) this.form.scanCodeFieldsReadOnly = this.form.scanCodeFieldsReadOnly.map(item => item.value)
      this.scanCodeFieldsReadOnlyDetail.Data1 = this.form.scanCodeFieldsReadOnly.toString()
      settings[i++] = this.scanCodeFieldsReadOnlyDetail

      if (this.scanCodeDelivFieldsHiddenDetail == null) {
        this.scanCodeDelivFieldsHiddenDetail = await CorporationDetail.loadFromUser('ScanCode_Deliv_Fields_Hidden')
      }

      if (this.form.scanCodeDelivFieldsHidden.toString().includes('[')) this.form.scanCodeDelivFieldsHidden = this.form.scanCodeDelivFieldsHidden.map(item => item.value)
      this.scanCodeDelivFieldsHiddenDetail.Data1 = this.form.scanCodeDelivFieldsHidden.toString()
      settings[i++] = this.scanCodeDelivFieldsHiddenDetail

      if (this.scanCodeDelivFieldsMandatoryDetail == null) {
        this.scanCodeDelivFieldsMandatoryDetail = await CorporationDetail.loadFromUser('ScanCode_Deliv_Fields_Mandatory')
      }

      if (this.form.scanCodeDelivFieldsMandatory.toString().includes('[')) this.form.scanCodeDelivFieldsMandatory = this.form.scanCodeDelivFieldsMandatory.map(item => item.value)
      this.scanCodeDelivFieldsMandatoryDetail.Data1 = this.form.scanCodeDelivFieldsMandatory.toString()
      settings[i++] = this.scanCodeDelivFieldsMandatoryDetail

      if (this.scanCodeDelivFieldsReadOnlyDetail == null) {
        this.scanCodeDelivFieldsReadOnlyDetail = await CorporationDetail.loadFromUser('ScanCode_Deliv_Fields_ReadOnly')
      }

      if (this.form.scanCodeDelivFieldsReadOnly.toString().includes('[')) this.form.scanCodeDelivFieldsReadOnly = this.form.scanCodeDelivFieldsReadOnly.map(item => item.value)
      this.scanCodeDelivFieldsReadOnlyDetail.Data1 = this.form.scanCodeDelivFieldsReadOnly.toString()
      settings[i++] = this.scanCodeDelivFieldsReadOnlyDetail

      if (this.preRegistrationFieldsHiddenDetail == null) {
        this.preRegistrationFieldsHiddenDetail = await CorporationDetail.loadFromUser('PreRegistration_Fields_Hidden')
      }

      if (this.form.preRegistrationFieldsHidden.toString().includes('[')) this.form.preRegistrationFieldsHidden = this.form.preRegistrationFieldsHidden.map(item => item.value)
      this.preRegistrationFieldsHiddenDetail.Data1 = this.form.preRegistrationFieldsHidden.toString()
      settings[i++] = this.preRegistrationFieldsHiddenDetail

      if (this.preRegistrationFieldsMandatoryDetail == null) {
        this.preRegistrationFieldsMandatoryDetail = await CorporationDetail.loadFromUser('PreRegistration_Fields_Mandatory')
      }

      if (this.form.preRegistrationFieldsMandatory.toString().includes('[')) this.form.preRegistrationFieldsMandatory = this.form.preRegistrationFieldsMandatory.map(item => item.value)
      this.preRegistrationFieldsMandatoryDetail.Data1 = this.form.preRegistrationFieldsMandatory.toString()
      settings[i++] = this.preRegistrationFieldsMandatoryDetail

      if (this.preRegistrationFieldsReadOnlyDetail == null) {
        this.preRegistrationFieldsReadOnlyDetail = await CorporationDetail.loadFromUser('PreRegistration_Fields_ReadOnly')
      }

      if (this.form.preRegistrationFieldsReadOnly.toString().includes('[')) this.form.preRegistrationFieldsReadOnly = this.form.preRegistrationFieldsReadOnly.map(item => item.value)
      this.preRegistrationFieldsReadOnlyDetail.Data1 = this.form.preRegistrationFieldsReadOnly.toString()
      settings[i++] = this.preRegistrationFieldsReadOnlyDetail

      if (this.preRegistrationGPSPositionDetail == null) {
        this.preRegistrationGPSPositionDetail = await CorporationDetail.loadFromUser('PreRegistration_GPSPosition')
      }

      this.preRegistrationGPSPositionDetail.Data1 = this.form.preRegistrationGPSPosition
      settings[i++] = this.preRegistrationGPSPositionDetail

      if (this.preRegistrationGPSAcceptedDistanceKmDetail == null) {
        this.preRegistrationGPSAcceptedDistanceKmDetail = await CorporationDetail.loadFromUser('PreRegistration_GPSAcceptedDistanceKm')
      }

      this.preRegistrationGPSAcceptedDistanceKmDetail.Data1 = this.form.preRegistrationGPSAcceptedDistanceKm
      settings[i++] = this.preRegistrationGPSAcceptedDistanceKmDetail

      if (this.scanMobileFieldsHiddenDetail == null) {
        this.scanMobileFieldsHiddenDetail = await CorporationDetail.loadFromUser('ScanMobile_Fields_Hidden')
      }

      if (this.form.scanMobileFieldsHidden.toString().includes('[')) this.form.scanMobileFieldsHidden = this.form.scanMobileFieldsHidden.map(item => item.value)
      this.scanMobileFieldsHiddenDetail.Data1 = this.form.scanMobileFieldsHidden.toString()
      settings[i++] = this.scanMobileFieldsHiddenDetail

      if (this.scanMobileFieldsMandatoryDetail == null) {
        this.scanMobileFieldsMandatoryDetail = await CorporationDetail.loadFromUser('ScanMobile_Fields_Mandatory')
      }

      if (this.form.scanMobileFieldsMandatory.toString().includes('[')) this.form.scanMobileFieldsMandatory = this.form.scanMobileFieldsMandatory.map(item => item.value)
      this.scanMobileFieldsMandatoryDetail.Data1 = this.form.scanMobileFieldsMandatory.toString()
      settings[i++] = this.scanMobileFieldsMandatoryDetail

      if (this.scanMobileFieldsReadOnlyDetail == null) {
        this.scanMobileFieldsReadOnlyDetail = await CorporationDetail.loadFromUser('ScanMobile_Fields_ReadOnly')
      }

      if (this.form.scanMobileFieldsReadOnly.toString().includes('[')) this.form.scanMobileFieldsReadOnly = this.form.scanMobileFieldsReadOnly.map(item => item.value)
      this.scanMobileFieldsReadOnlyDetail.Data1 = this.form.scanMobileFieldsReadOnly.toString()
      settings[i++] = this.scanMobileFieldsReadOnlyDetail

      if (this.scanMobileShowEnterButtonDetail == null) {
        this.scanMobileShowEnterButtonDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_Enter_Button')
      }

      this.scanMobileShowEnterButtonDetail.Data1 = this.form.scanMobileShowEnterButton.toString()
      settings[i++] = this.scanMobileShowEnterButtonDetail

      if (this.scanMobileShowFinishDialogDetail == null) {
        this.scanMobileShowFinishDialogDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_Finish_Dialog')
      }

      this.scanMobileShowFinishDialogDetail.Data1 = this.form.scanMobileShowFinishDialog.toString()
      settings[i++] = this.scanMobileShowFinishDialogDetail

      if (this.scanMobileShowUseTareDialogDetail == null) {
        this.scanMobileShowUseTareDialogDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_UseTare_Dialog')
      }

      this.scanMobileShowUseTareDialogDetail.Data1 = this.form.scanMobileShowUseTareDialog.toString()
      settings[i++] = this.scanMobileShowUseTareDialogDetail

      if (this.scanMobileShowRemoveTareDialogDetail == null) {
        this.scanMobileShowRemoveTareDialogDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_RemoveTare_Dialog')
      }

      this.scanMobileShowRemoveTareDialogDetail.Data1 = this.form.scanMobileShowRemoveTareDialog.toString()
      settings[i++] = this.scanMobileShowRemoveTareDialogDetail

      if (this.scanMobileShowCancelDialogDetail == null) {
        this.scanMobileShowCancelDialogDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_Cancel_Dialog')
      }

      this.scanMobileShowCancelDialogDetail.Data1 = this.form.scanMobileShowCancelDialog.toString()
      settings[i++] = this.scanMobileShowCancelDialogDetail

      if (this.classifierLoadRegistrationsDaysBackDetail == null) {
        this.classifierLoadRegistrationsDaysBackDetail = await CorporationDetail.loadFromUser('Classifier_LoadRegistrationsDaysBack')
      }

      this.classifierLoadRegistrationsDaysBackDetail.Data1 = this.form.classifierLoadRegistrationsDaysBack
      settings[i++] = this.classifierLoadRegistrationsDaysBackDetail

      if (this.scanMobileShowTransferRegDetail == null) {
        this.scanMobileShowTransferRegDetail = await CorporationDetail.loadFromUser('ScanMobile_Show_Transfer_RegistrationCheckbox')
      }

      this.scanMobileShowTransferRegDetail.Data1 = this.form.scanMobileShowTransferReg.toString()
      settings[i++] = this.scanMobileShowTransferRegDetail

      if (this.scanMobileDisableCreateVehicleDetail == null) {
        this.scanMobileDisableCreateVehicleDetail = await CorporationDetail.loadFromUser('ScanMobile_Disable_CreateVehicle')
      }

      this.scanMobileDisableCreateVehicleDetail.Data1 = this.form.scanMobileDisableCreateVehicle.toString()
      settings[i++] = this.scanMobileDisableCreateVehicleDetail

      if (this.ReportsShowOnlyOutGoingRegsDetail == null) {
        this.ReportsShowOnlyOutGoingRegsDetail = await CorporationDetail.loadFromUser('Reports_Show_OnlyOutGoingRegs_RegistrationCheckbox')
      }

      this.ReportsShowOnlyOutGoingRegsDetail.Data1 = this.form.ReportsShowOnlyOutGoingRegs.toString()
      settings[i++] = this.ReportsShowOnlyOutGoingRegsDetail

      if (this.ShowPrintConfirmationDetail == null) {
        this.ShowPrintConfirmationDetail = await CorporationDetail.loadFromUser('Reports_Show_PrintConfirmation_RegistrationCheckbox')
      }

      this.ShowPrintConfirmationDetail.Data1 = this.form.ShowPrintConfirmation.toString()
      settings[i++] = this.ShowPrintConfirmationDetail

      if (this.VehicleUserInheritFacilitiesDetail == null) {
        this.VehicleUserInheritFacilitiesDetail = await CorporationDetail.loadFromUser('CreateVehicle_ValidFacilities_Checkbox')
      }

      this.VehicleUserInheritFacilitiesDetail.Data1 = this.form.VehicleUserInheritFacilities.toString()
      settings[i++] = this.VehicleUserInheritFacilitiesDetail

      if (this.BatchRegistrationMinWeightWarningDetail == null) {
        this.BatchRegistrationMinWeightWarningDetail = await CorporationDetail.loadFromUser('BatchRegistration_MinWeightWarning')
      }

      this.BatchRegistrationMinWeightWarningDetail.Data1 = this.form.BatchRegistrationMinWeightWarning.toString()
      settings[i++] = this.BatchRegistrationMinWeightWarningDetail

      if (this.apiImportExportUrlDetail == null) {
        this.apiImportExportUrlDetail = await CorporationDetail.loadFromUser('ApiImportExportUrl')
      }

      this.apiImportExportUrlDetail.Data1 = this.form.apiImportExportUrl.toString()
      settings[i++] = this.apiImportExportUrlDetail

      if (this.apiKeyDetail == null) {
        this.apiKeyDetail = await CorporationDetail.loadFromUser('ApiKey')
        if (this.apiKeyDetail == null) {
          this.apiKeyDetail = await CorporationDetail.loadFromUser('apiKey')
        }
      }

      this.apiKeyDetail.Data1 = this.form.apiKey.toString()
      settings[i++] = this.apiKeyDetail

      if (this.apiImportExportGuidDetail == null) {
        this.apiImportExportGuidDetail = await CorporationDetail.loadFromUser('apiImportExportGuid')
      }

      this.apiImportExportGuidDetail.Data1 = this.form.apiImportExportGuid.toString()
      settings[i++] = this.apiImportExportGuidDetail

      if (this.apiImportIncludeDataDetail == null) {
        this.apiImportIncludeDataDetail = await CorporationDetail.loadFromUser('apiImportIncludeData')
      }

      this.apiImportIncludeDataDetail.Data1 = this.form.apiImportIncludeData.toString()
      settings[i++] = this.apiImportIncludeDataDetail

      if (this.preRegistrationExpiredTimeoutHoursDetail == null) {
        this.preRegistrationExpiredTimeoutHoursDetail = await CorporationDetail.loadFromUser('PreRegistration_ExpiredTimeoutHours')
      }

      this.preRegistrationExpiredTimeoutHoursDetail.Data1 = this.form.preRegistrationExpiredTimeoutHours
      settings[i++] = this.preRegistrationExpiredTimeoutHoursDetail

      if (this.wheelLoaderDisableButtonsDetail == null) {
        this.wheelLoaderDisableButtonsDetail = await CorporationDetail.loadFromUser('WheelLoader_Disable_Buttons')
      }

      this.wheelLoaderDisableButtonsDetail.Data1 = this.form.wheelLoaderDisableButtons.toString()
      settings[i++] = this.wheelLoaderDisableButtonsDetail

      if (this.wheelLoaderEnableVT25WeightIntegrationDetail == null) {
        this.wheelLoaderEnableVT25WeightIntegrationDetail = await CorporationDetail.loadFromUser('WheelLoader_Enable_VT25_Weight_Integration')
      }

      this.wheelLoaderEnableVT25WeightIntegrationDetail.Data1 = this.form.wheelLoaderEnableVT25WeightIntegration.toString()
      settings[i++] = this.wheelLoaderEnableVT25WeightIntegrationDetail

      if (this.wheelLoaderDisableCreateVehicleDetail == null) {
        this.wheelLoaderDisableCreateVehicleDetail = await CorporationDetail.loadFromUser('WheelLoader_Disable_CreateVehicle')
      }

      this.wheelLoaderDisableCreateVehicleDetail.Data1 = this.form.wheelLoaderDisableCreateVehicle.toString()
      settings[i++] = this.wheelLoaderDisableCreateVehicleDetail

      if (this.sysEnable2StepsAuthenticationDetail == null) {
        this.sysEnable2StepsAuthenticationDetail = await CorporationDetail.loadFromUser('Sys_Enable_2Steps_Authentication')
      }

      this.sysEnable2StepsAuthenticationDetail.Data1 = this.form.sysEnable2StepsAuthentication.toString()
      settings[i++] = this.sysEnable2StepsAuthenticationDetail

      if (this.sysUseFacilityDescriptionDetail == null) {
        this.sysUseFacilityDescriptionDetail = await CorporationDetail.loadFromUser('Sys_UseFacilityDescription')
      }

      this.sysUseFacilityDescriptionDetail.Data1 = this.form.sysUseFacilityDescription.toString()
      settings[i++] = this.sysUseFacilityDescriptionDetail

      if (this.sysMinPassWordLenghtDetail == null) {
        this.sysMinPassWordLenghtDetail = await CorporationDetail.loadFromUser('Sys_MinPassWordLengh')
      }

      this.sysMinPassWordLenghtDetail.Data1 = this.form.sysMinPassWordLenght.toString()
      settings[i++] = this.sysMinPassWordLenghtDetail

      if (this.sysPassWordRegularExpressionDetail == null) {
        this.sysPassWordRegularExpressionDetail = await CorporationDetail.loadFromUser('Sys_PassWordRegularExpression')
      }

      this.sysPassWordRegularExpressionDetail.Data1 = this.form.sysPassWordRegularExpression.toString()
      settings[i++] = this.sysPassWordRegularExpressionDetail

      if (this.deliveryInspectionHideSkipDetail === null) {
        this.deliveryInspectionHideSkipDetail = await CorporationDetail.loadFromUser('DeliveryInspection_Hide_Skip')
      }

      this.deliveryInspectionHideSkipDetail.Data1 = this.form.deliveryInspectionHideSkip.toString()
      settings[i++] = this.deliveryInspectionHideSkipDetail

      if (this.deliveryInspectionShowDisableDetail === null) {
        this.deliveryInspectionShowDisableDetail = await CorporationDetail.loadFromUser('DeliveryInspection_Show_Disable')
      }

      this.deliveryInspectionShowDisableDetail.Data1 = this.form.deliveryInspectionShowDisable.toString()
      settings[i++] = this.deliveryInspectionShowDisableDetail

      if (this.deliveryInspectionEmailHeaderDetail === null) {
        this.deliveryInspectionEmailHeaderDetail = await CorporationDetail.loadFromUser('DeliveryInspection_Email_Header')
      }

      this.deliveryInspectionEmailHeaderDetail.Data1 = this.form.deliveryInspectionEmailHeader.toString()
      settings[i++] = this.deliveryInspectionEmailHeaderDetail

      if (this.deliveryInspectionEmailBodyDetail === null) {
        this.deliveryInspectionEmailBodyDetail = await CorporationDetail.loadFromUser('DeliveryInspection_Email_Body')
      }

      this.deliveryInspectionEmailBodyDetail.Data1 = this.form.deliveryInspectionEmailBody.toString()
      settings[i++] = this.deliveryInspectionEmailBodyDetail

      if (this.deliveryInspectionEmailSenderDetail === null) {
        this.deliveryInspectionEmailSenderDetail = await CorporationDetail.loadFromUser('DeliveryInspection_Email_Sender')
      }

      this.deliveryInspectionEmailSenderDetail.Data1 = this.form.deliveryInspectionEmailSender.toString()
      settings[i++] = this.deliveryInspectionEmailSenderDetail

      if (this.scanMobileCamDelayDetail == null) {
        this.scanMobileCamDelayDetail = await CorporationDetail.loadFromUser('ScanMobile_CamDelay')
      }
      this.scanMobileCamDelayDetail.Data1 = this.form.scanMobileCamDelay.toString()
      settings[i++] = this.scanMobileCamDelayDetail

      if (this.scanMobileCamDelayEnabledDetail == null) {
        this.scanMobileCamDelayEnabledDetail = await CorporationDetail.loadFromUser('ScanMobile_CamDelayEnabled')
      }
      this.scanMobileCamDelayEnabledDetail.Data1 = this.form.scanMobileCamDelayEnabled.toString()
      settings[i++] = this.scanMobileCamDelayEnabledDetail

      await CorporationDetail.saveAllForUser(settings)
    },
    async loadSettings () {
      this.loading = true

      // Remember to add new claim check
      this.hasDeliveryInspectionClaim = Security.user.token.Claims.includes(License.deliveryInspectionNo)
      this.hasWheelLoaderClaim = Security.user.token.Claims.includes(License.wheelLoaderNo)
      this.hasPreRegistrationClaim = Security.user.token.Claims.includes(License.preRegistrationNo)
      this.hasScanCodeClaim = Security.user.token.Claims.includes(License.scanCodeNo)
      this.hasScanMobileClaim = Security.user.token.Claims.includes(License.scanMobileNo)
      this.hasClassifierClaim = Security.user.token.Claims.includes(License.classifierNo)
      this.hasReportsClaim = Security.user.token.Claims.includes(License.reportsNo)
      if (!this.hasReportsClaim) {
        this.hasReportsClaim = Security.user.token.Claims.includes(License.reportsSimpleNo)
      }

      this.hasBatchRegistrationClaim = Security.user.token.Claims.includes(License.batchRegistrationNo)
      this.hasApiImportExportClaim = Security.user.token.Claims.includes(License.apiImportExportNo)
      this.hasCreateVehicleClaim = Security.user.token.Claims.includes(License.createVehiclesNo)

      this.form.wheelLoaderFieldsHidden = []
      this.form.wheelLoaderFieldsMandatory = []
      this.form.wheelLoaderFieldsReadOnly = []
      this.form.wheelLoaderEnableVT25WeightIntegration = false
      this.form.wheelLoaderDisableButtons = false
      this.form.wheelLoaderDisableCreateVehicle = false

      this.form.scanCodeFieldsHidden = []
      this.form.scanCodeFieldsMandatory = []
      this.form.scanCodeFieldsReadOnly = []

      this.form.scanMobileFieldsHidden = []
      this.form.scanMobileFieldsMandatory = []
      this.form.scanMobileFieldsReadOnly = []
      this.form.scanMobileShowEnterButton = true
      this.form.scanMobileShowFinishDialog = true
      this.form.scanMobileShowUseTareDialog = true
      this.form.scanMobileShowRemoveTareDialog = true
      this.form.scanMobileShowCancelDialogDetail = true
      this.form.scanMobileShowTransferRegDetail = true
      this.form.scanMobileDisableCreateVehicle = true
      this.form.scanMobileCamDelay = 5
      this.form.scanMobileCamDelayEnabled = false

      this.form.ReportsShowOnlyOutGoingRegsDetail = false
      this.form.ReportsShowOnlyOutGoingRegs = false

      this.form.ShowPrintConfirmation = false
      this.form.ShowPrintConfirmationDetail = false

      // this.form.VehicleUserInheritFacilitiesDetail = false
      this.form.VehicleUserInheritFacilities = false

      this.form.BatchRegistrationMinWeightWarningDetail = 0
      this.form.BatchRegistrationMinWeightWarning = 0

      this.form.apiImportExportUrlDetail = 'https://domain_at_corp/api_at_corp'
      this.form.apiImportExportUrl = 'https://domain_at_corp/api_at_corp'
      this.form.apiImportExportGuidDetail = '00000000-0000-0000-0000-000000000000'
      this.form.apiImportExportGuid = '00000000-0000-0000-0000-000000000000'
      this.form.apiKeyDetail = '00000000-0000-0000-0000-000000000000'
      this.form.apiKey = '00000000-0000-0000-0000-000000000000'
      this.form.apiImportIncludeDataDetail = false
      this.form.apiImportIncludeData = false

      this.form.preRegistrationFieldsHidden = []
      this.form.preRegistrationFieldsMandatory = []
      this.form.preRegistrationFieldsReadOnly = []
      this.form.preRegistrationGPSPosition = null
      this.form.preRegistrationGPSAcceptedDistanceKm = null
      this.form.preRegistrationExpiredTimeoutHours = null

      const res = await CorporationDetail.loadAllFromUser()
      // console.log('loaded all ', res)
      for (let i = 0; i < res.length; i++) {
        switch (res[i].Name) {
          case 'Sys_Enable_2Steps_Authentication':
            this.sysEnable2StepsAuthenticationDetail = res[i]
            this.form.sysEnable2StepsAuthentication = this.sysEnable2StepsAuthenticationDetail.Data1 === 'true'
            break
          case 'Sys_UseFacilityDescription':
            this.sysUseFacilityDescriptionDetail = res[i]
            this.form.sysUseFacilityDescription = this.sysUseFacilityDescriptionDetail.Data1 === 'true'
            break
          case 'Sys_MinPassWordLengh':
            this.sysMinPassWordLenghtDetail = res[i]
            this.form.sysMinPassWordLenght = this.sysMinPassWordLenghtDetail.Data1
            break
          case 'Sys_PassWordRegularExpression':
            this.sysPassWordRegularExpressionDetail = res[i]
            this.form.sysPassWordRegularExpression = this.sysPassWordRegularExpressionDetail.Data1
            break
          case 'DeliveryInspection_Hide_Skip':
            this.deliveryInspectionHideSkipDetail = res[i]
            this.form.deliveryInspectionHideSkip = this.deliveryInspectionHideSkipDetail.Data1 === 'true'
            break
          case 'DeliveryInspection_Email_Header':
            this.deliveryInspectionEmailHeaderDetail = res[i]
            this.form.deliveryInspectionEmailHeader = this.deliveryInspectionEmailHeaderDetail.Data1
            break
          case 'DeliveryInspection_Email_Body':
            this.deliveryInspectionEmailBodyDetail = res[i]
            this.form.deliveryInspectionEmailBody = this.deliveryInspectionEmailBodyDetail.Data1
            break
          case 'DeliveryInspection_Email_Sender':
            this.deliveryInspectionEmailSenderDetail = res[i]
            this.form.deliveryInspectionEmailSender = this.deliveryInspectionEmailSenderDetail.Data1
            break
          case 'DeliveryInspection_Show_Disable':
            this.deliveryInspectionShowDisableDetail = res[i]
            this.form.deliveryInspectionShowDisable = this.deliveryInspectionShowDisableDetail.Data1 === 'true'
            break
          case 'WheelLoader_Fields_Hidden':
            this.wheelLoaderFieldsHiddenDetail = res[i]
            if (this.wheelLoaderFieldsHiddenDetail.Data1 || this.wheelLoaderFieldsHiddenDetail.Data1 === '') {
              this.form.wheelLoaderFieldsHidden = this.wheelLoaderFieldsHiddenDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            } else {
              this.form.wheelLoaderFieldsHidden = ['RequisitionNo2', 'Plant', 'Carrier', 'Comment', 'Storage'] // New fields default hidden LSV/MITH. Remember to also add new defaults in wheel-loader Registration/Registrations and Pre-Reg ActivePreRegistration/ExpiredPreRegistration
            }
            break
          case 'WheelLoader_Fields_Mandatory':
            this.wheelLoaderFieldsMandatoryDetail = res[i]
            this.form.wheelLoaderFieldsMandatory = this.wheelLoaderFieldsMandatoryDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'WheelLoader_Fields_ReadOnly':
            this.wheelLoaderFieldsReadOnlyDetail = res[i]
            this.form.wheelLoaderFieldsReadOnly = this.wheelLoaderFieldsReadOnlyDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'WheelLoader_Enable_VT25_Weight_Integration':
            this.wheelLoaderEnableVT25WeightIntegrationDetail = res[i]
            this.form.wheelLoaderEnableVT25WeightIntegration = this.wheelLoaderEnableVT25WeightIntegrationDetail.Data1 === 'true'
            break
          case 'WheelLoader_Disable_Buttons':
            this.wheelLoaderDisableButtonsDetail = res[i]
            this.form.wheelLoaderDisableButtons = this.wheelLoaderDisableButtonsDetail.Data1 === 'true'
            break
          case 'WheelLoader_Disable_CreateVehicle':
            this.wheelLoaderDisableCreateVehicleDetail = res[i]
            this.form.wheelLoaderDisableCreateVehicle = this.wheelLoaderDisableCreateVehicleDetail.Data1 === 'true'
            break
          case 'ScanCode_Fields_Hidden':
            this.scanCodeFieldsHiddenDetail = res[i]
            this.form.scanCodeFieldsHidden = this.scanCodeFieldsHiddenDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanCode_Fields_Mandatory':
            this.scanCodeFieldsMandatoryDetail = res[i]
            this.form.scanCodeFieldsMandatory = this.scanCodeFieldsMandatoryDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanCode_Fields_ReadOnly':
            this.scanCodeFieldsReadOnlyDetail = res[i]
            this.form.scanCodeFieldsReadOnly = this.scanCodeFieldsReadOnlyDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanCode_Deliv_Fields_Hidden':
            this.scanCodeDelivFieldsHiddenDetail = res[i]
            this.form.scanCodeDelivFieldsHidden = this.scanCodeDelivFieldsHiddenDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanCode_Deliv_Fields_Mandatory':
            this.scanCodeDelivFieldsMandatoryDetail = res[i]
            this.form.scanCodeDelivFieldsMandatory = this.scanCodeDelivFieldsMandatoryDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanCode_Deliv_Fields_ReadOnly':
            this.scanCodeDelivFieldsReadOnlyDetail = res[i]
            this.form.scanCodeDelivFieldsReadOnly = this.scanCodeDelivFieldsReadOnlyDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'PreRegistration_Fields_Hidden':
            this.preRegistrationFieldsHiddenDetail = res[i]
            this.form.preRegistrationFieldsHidden = this.preRegistrationFieldsHiddenDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'PreRegistration_Fields_Mandatory':
            this.preRegistrationFieldsMandatoryDetail = res[i]
            this.form.preRegistrationFieldsMandatory = this.preRegistrationFieldsMandatoryDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'PreRegistration_Fields_ReadOnly':
            this.preRegistrationFieldsReadOnlyDetail = res[i]
            this.form.preRegistrationFieldsReadOnly = this.preRegistrationFieldsReadOnlyDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'PreRegistration_GPSPosition':
            this.preRegistrationGPSPositionDetail = res[i]
            if (this.preRegistrationGPSPositionDetail.Data1) {
              this.form.preRegistrationGPSPosition = this.preRegistrationGPSPositionDetail.Data1
            } else {
              // Default position Scanvaegt Systems A/S (latitude, longitude) from Google Maps | Right click | copy to clipboard
              this.form.preRegistrationGPSPosition = '56.2050473652695, 10.185711838787292'
            }
            break
          case 'PreRegistration_GPSAcceptedDistanceKm':
            this.preRegistrationGPSAcceptedDistanceKmDetail = res[i]
            if (this.preRegistrationGPSAcceptedDistanceKmDetail.Data1) {
              this.form.preRegistrationGPSAcceptedDistanceKm = this.preRegistrationGPSAcceptedDistanceKmDetail.Data1
            } else {
              // Default accepted distance 0.2 km
              this.form.preRegistrationGPSAcceptedDistanceKm = 0.2
            }
            break
          case 'PreRegistration_ExpiredTimeoutHours':
            this.preRegistrationExpiredTimeoutHoursDetail = res[i]
            if (this.preRegistrationExpiredTimeoutHoursDetail.Data1) {
              this.form.preRegistrationExpiredTimeoutHours = this.preRegistrationExpiredTimeoutHoursDetail.Data1
            } else {
              // Default expiring timeout awaiting QR Scanning or GPS
              this.form.preRegistrationExpiredTimeoutHours = 12
            }
            break
          case 'ScanMobile_Fields_Hidden':
            this.scanMobileFieldsHiddenDetail = res[i]
            if (this.scanMobileFieldsHiddenDetail.Data1 || this.scanMobileFieldsHiddenDetail.Data1 === '') {
              this.form.scanMobileFieldsHidden = this.scanMobileFieldsHiddenDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            } else {
              this.form.scanMobileFieldsHidden = ['Storage']
            }
            break
          case 'ScanMobile_Fields_Mandatory':
            this.scanMobileFieldsMandatoryDetail = res[i]
            this.form.scanMobileFieldsMandatory = this.scanMobileFieldsMandatoryDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanMobile_Fields_ReadOnly':
            this.scanMobileFieldsReadOnlyDetail = res[i]
            this.form.scanMobileFieldsReadOnly = this.scanMobileFieldsReadOnlyDetail.Data1.split(',').map(item => { return { text: item, value: item } })
            break
          case 'ScanMobile_Show_Enter_Button':
            this.scanMobileShowEnterButtonDetail = res[i]
            this.form.scanMobileShowEnterButton = this.scanMobileShowEnterButtonDetail.Data1 === 'true'
            break
          case 'ScanMobile_Show_Finish_Dialog':
            this.scanMobileShowFinishDialogDetail = res[i]
            this.form.scanMobileShowFinishDialog = this.scanMobileShowFinishDialogDetail.Data1 === 'true'
            break
          case 'ScanMobile_Show_Cancel_Dialog':
            this.scanMobileShowCancelDialogDetail = res[i]
            this.form.scanMobileShowCancelDialog = this.scanMobileShowCancelDialogDetail.Data1 === 'true'
            break
          case 'ScanMobile_Show_UseTare_Dialog':
            this.scanMobileShowUseTareDialogDetail = res[i]
            this.form.scanMobileShowUseTareDialog = this.scanMobileShowUseTareDialogDetail.Data1 === 'true'
            break
          case 'ScanMobile_Show_RemoveTare_Dialog':
            this.scanMobileShowRemoveTareDialogDetail = res[i]
            this.form.scanMobileShowRemoveTareDialog = this.scanMobileShowRemoveTareDialogDetail.Data1 === 'true'
            break
          case 'ScanMobile_Disable_CreateVehicle':
            this.scanMobileDisableCreateVehicleDetail = res[i]
            this.form.scanMobileDisableCreateVehicle = this.scanMobileDisableCreateVehicleDetail.Data1 === 'true'
            break
          case 'ScanMobile_CamDelay':
            this.scanMobileCamDelayDetail = res[i]
            if (this.scanMobileCamDelayDetail.Data1) {
              this.form.scanMobileCamDelay = this.scanMobileCamDelayDetail.Data1
            } else {
              this.form.scanMobileCamDelay = 5
            }
            break
          case 'ScanMobile_CamDelayEnabled':
            this.scanMobileCamDelayEnabledDetail = res[i]
            this.form.scanMobileCamDelayEnabled = this.scanMobileCamDelayEnabledDetail.Data1 === 'true'
            break
          case 'Classifier_LoadRegistrationsDaysBack':
            this.classifierLoadRegistrationsDaysBackDetail = res[i]
            if (this.classifierLoadRegistrationsDaysBackDetail.Data1) {
              this.form.classifierLoadRegistrationsDaysBack = this.classifierLoadRegistrationsDaysBackDetail.Data1
            } else {
              this.form.classifierLoadRegistrationsDaysBack = 7
            }
            break
          case 'ScanMobile_Show_Transfer_RegistrationCheckbox':
            this.scanMobileShowTransferRegDetail = res[i]
            this.form.scanMobileShowTransferReg = this.scanMobileShowTransferRegDetail.Data1 === 'true'
            break
          case 'Reports_Show_OnlyOutGoingRegs_RegistrationCheckbox':
            this.ReportsShowOnlyOutGoingRegsDetail = res[i]
            this.form.ReportsShowOnlyOutGoingRegs = this.ReportsShowOnlyOutGoingRegsDetail.Data1 === 'true'
            break
          case 'Reports_Show_PrintConfirmation_RegistrationCheckbox':
            this.ShowPrintConfirmationDetail = res[i]
            this.form.ShowPrintConfirmation = this.ShowPrintConfirmationDetail.Data1 === 'true'
            break
          case 'CreateVehicle_ValidFacilities_Checkbox':
            this.VehicleUserInheritFacilitiesDetail = res[i]
            this.form.VehicleUserInheritFacilities = this.VehicleUserInheritFacilitiesDetail.Data1 === 'true'
            break
          case 'BatchRegistration_MinWeightWarning':
            this.BatchRegistrationMinWeightWarningDetail = res[i]
            this.form.BatchRegistrationMinWeightWarning = this.BatchRegistrationMinWeightWarningDetail.Data1
            break
          case 'ApiImportExportUrl':
            this.apiImportExportUrlDetail = res[i]
            this.form.apiImportExportUrl = this.apiImportExportUrlDetail.Data1
            break
          case 'apiImportExportGuid':
            this.apiImportExportGuidDetail = res[i]
            this.form.apiImportExportGuid = this.apiImportExportGuidDetail.Data1
            break
          case 'ApiKey':
          case 'apiKey':
            // console.log('loaded ', res[i], i)

            this.apiKeyDetail = res[i]
            this.form.apiKey = this.apiKeyDetail.Data1
            break
          case 'apiImportIncludeData':
            this.apiImportIncludeDataDetail = res[i]
            this.form.apiImportIncludeData = this.apiImportIncludeDataDetail.Data1 === 'true'
            break
        }
      }
    },
    generateGUID () {
      var dt = new Date().getTime()
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,
        function (c) {
          var rnd = Math.random() * 16// random number in range 0 to 16
          rnd = (dt + rnd) % 16 | 0
          dt = Math.floor(dt / 16)
          return (c === 'x' ? rnd : (rnd & 0x3 | 0x8)).toString(16)
        })
    },
    setGuid () {
      this.form.apiKey = this.generateGUID()
    }
  },

  async mounted () {
    await this.loadSettings()

    this.loading = false

    // We use next tick to ensure that app is initialized
    this.$nextTick(() => {
      app.$on('force-update', () => {
        // We need this to ensure that autoLogin re-renders component
        this.$forceUpdate()
      })
    })
  }
}
</script>
